import React from 'react';
import {
    Drawer,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    IconButton,
    Toolbar,
    Divider,
    Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Helper function to format Unix timestamp (seconds)
const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Invalid Date';
    try {
        const date = new Date(timestamp * 1000); // Multiply by 1000 if timestamp is in seconds
        return date.toLocaleString();
    } catch (e) {
        console.error("Error formatting timestamp:", e);
        return 'Invalid Date';
    }
};

// Helper function to parse information JSON and extract key details
const parseInformation = (category, infoString) => {
    let details = {};
    let parsedInfo = {};
    if (typeof infoString === 'string') {
        try {
            parsedInfo = JSON.parse(infoString);
        } catch (e) {
            console.error("Error parsing information JSON:", e);
            return { error: "Could not parse event details." };
        }
    }

    // Extract relevant info based on category
    switch (category) {
        case 'created':
            details.serialId = parsedInfo.serial_id;
            details.type = parsedInfo.machine_type;
            break;
        case 'updated':
            // Maybe list changed fields if 'old_settings' and 'update' exist?
            // For simplicity, just show that an update occurred.
            details.summary = "Machine details updated.";
            break;
        case 'machine_move_swap':
        case 'swap': // Handle both swap types
            details.machineIn = parsedInfo.machine_coming_in || parsedInfo.machine_to_venue;
            details.machineOut = parsedInfo.machine_going_out || parsedInfo.machine_to_warehouse;
            details.fromVenue = parsedInfo.from_venue_id;
            details.toVenue = parsedInfo.to_venue_id;
            details.summary = `Swap: Machine ${details.machineIn || '?'} moved to venue ${details.toVenue || '?'}. Machine ${details.machineOut || '?'} removed.`;
            break;
        case 'machine_move_install':
            details.machineInstalled = parsedInfo.machine_installed;
            details.toVenue = parsedInfo.to_venue_id;
            details.summary = `Installed machine ${details.machineInstalled || '?'} at venue ${details.toVenue || '?'}.`;
            break;
        case 'machine_move_removal':
            details.machineRemoved = parsedInfo.machine_being_swapped; // Name from CSV data
            details.fromVenue = parsedInfo.from_venue_id;
            details.toWarehouse = parsedInfo.to_venue_id; // Target is usually warehouse
            details.summary = `Removed machine ${details.machineRemoved || '?'} from venue ${details.fromVenue || '?'}. Moved to warehouse ${details.toWarehouse || '?'}.`;
            break;
        // Add more cases as needed
        default:
            details.summary = "Details available in raw data.";
            break;
    }
    return details;
};

export default function MachineHistoryDrawer({ 
    open, 
    onClose, 
    machinePk, 
    contentType, // Currently only 'history' is fully handled
    data, 
    loading,
    machineLabel // Added to show in the title
}) {

    const renderContent = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading history...</Typography>
                </Box>
            );
        }

        if (data?.error) {
            return <Typography color="error" sx={{ p: 2 }}>Error: {data.error}</Typography>;
        }

        if (contentType === 'history') {
            // Check if data is an array and has items
            if (Array.isArray(data) && data.length > 0) {
                return (
                    <List dense>
                        {data.map((event) => {
                            // Parse the information field
                            const eventDetails = parseInformation(event.history_category, event.information);

                            return (
                                <React.Fragment key={event.history_id}> {/* Use history_id as key */} 
                                    <ListItem alignItems="flex-start">
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                    <Chip label={event.history_category || 'Unknown Event'} size="small" sx={{ mr: 1 }} />
                                                    <Typography variant="body2" color="text.secondary"> 
                                                        {/* Use account_name if available, fallback to user_id */}
                                                        User: {event.account_name || event.user_id || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={
                                                <>
                                                    <Typography
                                                        sx={{ display: 'block' }} // Display timestamp on its own line
                                                        component="span"
                                                        variant="body2"
                                                        color="text.primary"
                                                    >
                                                        {formatTimestamp(event.time)} {/* Format Unix timestamp */}
                                                    </Typography>
                                                    {/* Display extracted details or error */} 
                                                    {eventDetails.error ? (
                                                         <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'error.main' }}>{eventDetails.error}</Typography>
                                                    ) : (
                                                         eventDetails.summary && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{eventDetails.summary}</Typography>
                                                    )}
                                                    {/* Optionally add a way to view raw JSON if needed */}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                    {data.indexOf(event) < data.length - 1 && <Divider component="li" variant="inset" />} 
                                </React.Fragment>
                            );
                        })}
                    </List>
                );
            } else {
                return <Typography sx={{ p: 2, color: 'text.secondary' }}>No history events found for this machine.</Typography>;
            }
        } 

        // Placeholder for other content types (e.g., notes)
        if (contentType === 'notes') {
             return <Typography sx={{ p: 2, color: 'text.secondary' }}>Notes view not implemented yet.</Typography>;
        } 

        return <Typography sx={{ p: 2, color: 'text.secondary' }}>Select content type.</Typography>; // Default fallback
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: '90%', maxWidth: 600 } // Increased width slightly
            }}
        >
            <Toolbar sx={{ justifyContent: 'space-between' }}>
                <Typography variant="h6">
                    {contentType === 'history' ? 'History for:' : 'Details for:'} {machineLabel || `Machine ${machinePk}`}
                </Typography>
                <IconButton edge="end" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Toolbar>
            <Divider />
            <Box sx={{ overflowY: 'auto' }}> 
                {renderContent()}
            </Box>
        </Drawer>
    );
} 